import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  ShieldCheck,
  Package,
  Truck,
  MapPin,
  Pencil,
  XCircle,
  ChevronDown,
  ChevronUp,
  Home,
  Building2,
  Calendar,
  Info,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const AUTO_CONFIRM_SECONDS = 30;
const LS_ORDERS = "dyntra_recent_orders_v1";
const LS_ADDRESSES = "dyntra_addresses_v1";

function loadOrders() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_ORDERS) || "{}");
    return typeof raw === "object" && raw ? raw : {};
  } catch {
    return {};
  }
}
function saveOrder(orderNo, patch) {
  try {
    const orders = loadOrders();
    orders[orderNo] = { ...(orders[orderNo] || {}), ...patch };
    localStorage.setItem(LS_ORDERS, JSON.stringify(orders));
  } catch {}
}
function loadAddresses() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_ADDRESSES) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

const PROGRESS_STEPS = [
  { key: "received", label: "Order received", icon: CheckCircle2 },
  { key: "awaiting", label: "Awaiting confirmation", icon: Clock },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
];

function formatShortTime(d) {
  try {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return "";
  }
}
function formatShortDate(d) {
  try {
    return d.toLocaleDateString([], { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}
function formatLongDate(d) {
  try {
    return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function OrderConfirmation() {
  const { orderNo } = useParams();
  const nav = useNavigate();

  const [remaining, setRemaining] = useState(AUTO_CONFIRM_SECONDS);
  const [state, setState] = useState("awaiting");
  const [order, setOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editAddrOpen, setEditAddrOpen] = useState(false);
  const [orderPlacedAt] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderNo}`);
        if (!cancelled) setOrder(data);
      } catch {
        const addresses = loadAddresses();
        setOrder({
          number: orderNo,
          items: [],
          address: addresses.find((a) => a.is_default) || addresses[0] || null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderNo]);

  useEffect(() => {
    if (state !== "awaiting") return;
    if (remaining <= 0) {
      setState("confirmed");
      saveOrder(orderNo, { state: "confirmed", confirmed_at: new Date().toISOString() });
      return;
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, state, orderNo]);

  const activeStepIdx = useMemo(() => {
    if (state === "confirmed") return 2;
    if (state === "cancelled") return 1;
    return 1;
  }, [state]);

  const confirmNow = () => {
    setState("confirmed");
    saveOrder(orderNo, { state: "confirmed", confirmed_at: new Date().toISOString() });
    toast.success("Order confirmed");
  };

  const cancelOrder = async () => {
    if (state !== "awaiting") return;
    try {
      await api.post(`/orders/${orderNo}/cancel`, {}).catch(() => {});
    } catch {}
    setState("cancelled");
    saveOrder(orderNo, { state: "cancelled", cancelled_at: new Date().toISOString() });
    toast.info("Order cancelled");
    setTimeout(() => nav("/cart", { replace: true }), 1500);
  };

  const modifyOrder = () => {
    if (state !== "awaiting") return;
    toast.info("Redirecting to cart to modify your order…");
    setTimeout(() => nav("/cart", { replace: true }), 800);
  };

  const percentage = Math.max(
    0,
    Math.min(100, (remaining / AUTO_CONFIRM_SECONDS) * 100)
  );
  const dashArray = 2 * Math.PI * 42;
  const dashOffset = dashArray * (1 - percentage / 100);

  const items = order?.items || order?.lines || [];
  const totals = order?.totals || {};
  const address = order?.address || null;

  const itemCount = items.reduce((s, i) => s + (i.quantity || 1), 0) || items.length;

  const subtotal =
    totals.itemsTotal || items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
  const delivery = totals.deliveryFee || 0;
  const discount = totals.discount || 0;
  const grand = totals.grandTotal || subtotal + delivery - discount;

  const expectedDelivery = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 5);
    return d;
  }, []);

  return (
    <div className="dyntra-container py-8" data-testid="order-confirmation">
      {/* Success banner (centered like mockup) */}
      <div className="flex flex-col items-center text-center">
        <div className="h-14 w-14 rounded-full bg-white ring-4 ring-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold text-gray-900">
          {state === "cancelled" ? "Order cancelled" : "Payment successful"}
        </h1>
        {state !== "cancelled" && (
          <p className="text-[#4B2AAD] font-semibold text-sm mt-1">
            Your order has been received
          </p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Order ID: <span className="text-gray-800 font-semibold">{orderNo}</span>
        </p>
        <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 font-bold text-xs px-3 py-1 border border-emerald-100">
          <ShieldCheck className="h-3.5 w-3.5" /> Payment confirmed
        </span>
      </div>

      {/* Countdown / Actions - full width */}
      <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-5 sm:p-6">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="relative h-28 w-28 shrink-0">
            <svg width="112" height="112" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="42"
                fill="none"
                stroke="#F3EFFC"
                strokeWidth="8"
              />
              <circle
                cx="48"
                cy="48"
                r="42"
                fill="none"
                stroke="#4B2AAD"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 48 48)"
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p
                data-testid="countdown-seconds"
                className="font-display text-2xl font-extrabold text-gray-900 leading-none"
              >
                {state === "awaiting"
                  ? String(Math.floor(remaining / 60)).padStart(2, "0") +
                    ":" +
                    String(remaining % 60).padStart(2, "0")
                  : "✓"}
              </p>
              <p className="text-[10px] text-gray-500 font-semibold mt-1">
                {state === "awaiting" ? "seconds" : state}
              </p>
              <p className="text-[9px] text-gray-400 leading-none">
                {state === "awaiting" ? "remaining" : ""}
              </p>
            </div>
          </div>

          <div className="flex-1 min-w-[240px]">
            <h2 className="font-display text-lg font-bold text-gray-900">
              {state === "awaiting"
                ? "Confirm your order"
                : state === "confirmed"
                  ? "Order confirmed"
                  : "Order cancelled"}
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">
              {state === "awaiting"
                ? "Your order will be confirmed automatically when the timer ends. Changes will no longer be available."
                : state === "confirmed"
                  ? "We've started preparing your order. You'll receive shipment updates by SMS and email."
                  : "The order has been cancelled and any charges will be refunded."}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                data-testid="oc-confirm"
                disabled={state !== "awaiting"}
                onClick={confirmNow}
                className="inline-flex items-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] text-white text-sm font-bold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle2 className="h-4 w-4" /> Confirm Order
              </button>
              <button
                data-testid="oc-modify"
                disabled={state !== "awaiting"}
                onClick={modifyOrder}
                className="inline-flex items-center gap-2 rounded-xl border border-[#4B2AAD]/40 text-[#4B2AAD] hover:bg-[#F3EFFC] text-sm font-semibold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Pencil className="h-4 w-4" /> Modify Order
              </button>
              <button
                data-testid="oc-cancel"
                disabled={state !== "awaiting"}
                onClick={cancelOrder}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <XCircle className="h-4 w-4" /> Cancel Order
              </button>
            </div>
          </div>
        </div>

        {/* Linear progress under countdown */}
        <div className="mt-5 h-1.5 rounded-full bg-[#F3EFFC] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#4B2AAD] to-[#6B4CD9] transition-all duration-1000"
            style={{ width: `${100 - percentage}%` }}
          />
        </div>
      </div>

      {/* Two-column: Order Summary (left) + Address + Progress (right) */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Order Summary */}
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-bold text-gray-900 inline-flex items-center gap-2">
              <Package className="h-4 w-4 text-[#4B2AAD]" /> Order Summary
            </h3>
            <span className="text-xs text-gray-500">
              {itemCount || items.length} item{(itemCount || items.length) === 1 ? "" : "s"}
            </span>
          </div>

          {items.length > 0 ? (
            <div className="space-y-3 max-h-56 overflow-y-auto thin-scroll">
              {items.map((it, i) => (
                <div key={i} className="flex gap-3 items-center text-sm">
                  <img
                    src={it.image || "/product-placeholder.svg"}
                    alt=""
                    className="h-12 w-12 rounded-lg object-contain bg-[#F5F3FB] shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 line-clamp-1">
                      {it.name || it.productName}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      Qty: {it.quantity}
                    </p>
                  </div>
                  <p className="font-bold text-gray-900 whitespace-nowrap">
                    ₹{((it.price || 0) * (it.quantity || 1)).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Your order details will be shown here once loaded.
            </p>
          )}

          <div className="mt-4 border-t border-dashed border-gray-200 pt-3 space-y-1 text-sm">
            <Row label="Item total" value={`₹${subtotal.toLocaleString()}`} />
            {discount > 0 && (
              <Row label="Discount" value={`-₹${discount.toLocaleString()}`} tone="purple" />
            )}
            <Row
              label="Delivery"
              value={delivery === 0 ? "FREE" : `₹${delivery.toLocaleString()}`}
              tone={delivery === 0 ? "green" : "default"}
            />
            <div className="flex items-center justify-between font-display font-bold text-base pt-2 border-t border-gray-100">
              <span>Grand Total</span>
              <span className="text-gray-900">₹{grand.toLocaleString()}</span>
            </div>
          </div>

          <button
            onClick={() => setDetailsOpen((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-[#4B2AAD] hover:border-[#4B2AAD] hover:bg-[#F3EFFC]"
          >
            {detailsOpen ? "Hide details" : "View order details"}
            {detailsOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>

          {detailsOpen && (
            <div className="mt-3 rounded-xl bg-[#F5F3FB] p-3 text-xs text-gray-600">
              Placed at {formatShortTime(orderPlacedAt)} on {formatShortDate(orderPlacedAt)}.
              A confirmation email has been sent to your registered address.
            </div>
          )}
        </div>

        {/* Right column: Address + Progress */}
        <div className="space-y-4">
          {/* Address */}
          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-base font-bold text-gray-900 inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#4B2AAD]" /> Delivery Address
              </h3>
              <button
                onClick={() => setEditAddrOpen(true)}
                data-testid="oc-edit-address"
                disabled={state !== "awaiting"}
                className="inline-flex items-center gap-1 rounded-xl border border-[#4B2AAD]/40 text-[#4B2AAD] text-xs font-semibold px-3 py-1.5 hover:bg-[#F3EFFC] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit Address
              </button>
            </div>

            <div className="mt-3 flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
                {address?.type === "Clinic" ? (
                  <Building2 className="h-5 w-5 text-[#4B2AAD]" />
                ) : (
                  <Home className="h-5 w-5 text-[#4B2AAD]" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900">
                  {address?.name || address?.full_name || "Delivery Address"}
                </p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {address ? (
                    <>
                      {address.line1}
                      {address.line2 ? `, ${address.line2}` : ""},<br />
                      {address.city}, {address.state} – {address.pincode}
                      <br />
                      {address.phone}
                    </>
                  ) : (
                    "Address will be finalised by our team."
                  )}
                </p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {address?.type && (
                    <span className="inline-flex items-center rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-2 py-0.5">
                      {address.type}
                    </span>
                  )}
                  {address?.is_default && (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 border border-emerald-100">
                      Default
                    </span>
                  )}
                </div>
              </div>
            </div>

            {state === "awaiting" && (
              <div className="mt-3 rounded-xl bg-[#F5F3FB] border border-[#4B2AAD]/10 px-3 py-2 text-xs text-gray-600 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 text-[#4B2AAD] shrink-0 mt-0.5" />
                Review or edit your delivery address before the countdown ends.
              </div>
            )}
          </div>

          {/* Order Progress - horizontal */}
          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <h3 className="font-display text-base font-bold text-gray-900 inline-flex items-center gap-2 mb-4">
              <Truck className="h-4 w-4 text-[#4B2AAD]" /> Order Progress
            </h3>

            <div className="grid grid-cols-4 gap-2">
              {PROGRESS_STEPS.map((s, i) => {
                const done = state === "confirmed" ? i <= activeStepIdx : i < activeStepIdx;
                const active =
                  i === activeStepIdx && state !== "cancelled" && !done;
                const meta =
                  i === 0
                    ? `${formatShortTime(orderPlacedAt)}, ${formatShortDate(orderPlacedAt)}`
                    : i === 1 && active
                      ? "Within 30 sec"
                      : "Upcoming";
                return (
                  <div key={s.key} className="text-center">
                    <div className="relative flex items-center justify-center">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          done
                            ? "bg-emerald-500 text-white"
                            : active
                              ? "bg-white border-2 border-[#4B2AAD] text-[#4B2AAD] ring-4 ring-[#F3EFFC]"
                              : "bg-gray-100 text-gray-300"
                        }`}
                      >
                        {done ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-current" />
                        )}
                      </div>
                      {i < PROGRESS_STEPS.length - 1 && (
                        <div
                          className={`absolute top-1/2 left-full -translate-y-1/2 w-full h-[2px] ${
                            done ? "bg-emerald-500" : "bg-gray-200 border-t border-dashed"
                          }`}
                        />
                      )}
                    </div>
                    <p
                      className={`mt-2 text-[11px] font-semibold ${
                        done || active ? "text-gray-900" : "text-gray-400"
                      }`}
                    >
                      {s.label}
                    </p>
                    <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                      {meta}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#F5F3FB] border border-[#4B2AAD]/10 px-3 py-2 text-xs text-gray-700">
              <Calendar className="h-4 w-4 text-[#4B2AAD]" />
              <span className="text-gray-500">Expected delivery:</span>
              <span className="font-bold text-[#4B2AAD]">
                {formatLongDate(expectedDelivery)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="mt-4 rounded-2xl bg-[#F5F3FB] border border-[#4B2AAD]/10 p-3 flex items-center gap-3 text-xs text-gray-600">
        <ShieldCheck className="h-4 w-4 text-[#4B2AAD] shrink-0" />
        Your payment is secure. Any eligible refund will be returned to the
        original payment method.
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link
          to="/account/orders"
          className="inline-flex items-center gap-2 rounded-xl bg-[#4B2AAD] text-white text-sm font-bold px-5 py-2.5 hover:bg-[#361D82]"
        >
          View My Orders
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-xl border border-[#4B2AAD]/40 text-[#4B2AAD] text-sm font-semibold px-5 py-2.5 hover:bg-[#F3EFFC]"
        >
          Continue Shopping
        </Link>
      </div>

      <EditAddressDialog
        open={editAddrOpen}
        onOpenChange={setEditAddrOpen}
        current={address}
        onSave={(next) => {
          setOrder((o) => ({ ...(o || {}), address: next }));
          toast.success("Address updated for this order");
        }}
      />
    </div>
  );
}

function Row({ label, value, tone }) {
  const toneCls =
    tone === "green"
      ? "text-emerald-600 font-semibold"
      : tone === "purple"
        ? "text-[#4B2AAD] font-semibold"
        : "text-gray-900 font-semibold";
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={toneCls}>{value}</span>
    </div>
  );
}

function EditAddressDialog({ open, onOpenChange, current, onSave }) {
  const [form, setForm] = useState(() => current || {});
  useEffect(() => {
    setForm(current || {});
  }, [current]);
  const set = (k, v) => setForm((f) => ({ ...(f || {}), [k]: v }));

  const save = () => {
    onSave(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Address</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full Name" value={form.name || form.full_name || ""} onChange={(v) => set("name", v)} />
          <Field label="Phone" value={form.phone || ""} onChange={(v) => set("phone", v)} />
          <Field full label="Address Line 1" value={form.line1 || ""} onChange={(v) => set("line1", v)} />
          <Field full label="Address Line 2" value={form.line2 || ""} onChange={(v) => set("line2", v)} />
          <Field label="City" value={form.city || ""} onChange={(v) => set("city", v)} />
          <Field label="State" value={form.state || ""} onChange={(v) => set("state", v)} />
          <Field label="Pincode" value={form.pincode || ""} onChange={(v) => set("pincode", v)} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="dyntra-btn-ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button className="dyntra-btn-primary" onClick={save}>
            Save Changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}
